<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import Swal from 'sweetalert2'
import AdminLayout from '@/layouts/AdminLayout.vue'
import UsuarioAdminForm, { type UsuarioAdminFormData } from '@/components/UsuarioAdminForm.vue'
import { getUsuario, updateUsuario } from '@/api/usuarios'
import { listProjetos } from '@/api/projetos'
import { getApiErrorMessage } from '@/api/client'
import { useAuth } from '@/composables/useAuth'
import type { UsuarioTipo } from '@/types/api'
import type { Projeto } from '@/types/projetos'

definePage({
  meta: {
    requiresAuth: true,
    requiresAdmin: true,
  },
})

const route = useRoute()
const router = useRouter()
const { usuario: usuarioLogado } = useAuth()

const usuarioId = computed(() => {
  const { id } = route.params as { id?: string | string[] }
  return Array.isArray(id) ? id[0] ?? '' : id ?? ''
})

const isSelf = computed(() => usuarioLogado.value?.id === usuarioId.value)

const loading = ref(true)
const saving = ref(false)
const projetos = ref<Projeto[]>([])

const tiposDisponiveis = computed<UsuarioTipo[]>(() => {
  if (usuarioLogado.value?.tipo === 'Master') {
    return ['Master', 'Admin']
  }

  return ['Admin']
})

const form = ref<UsuarioAdminFormData>({
  nome: '',
  login: '',
  senha: '',
  tipo: 'Admin',
  ativo: true,
  projeto_ids: [],
})

async function loadUsuario(): Promise<void> {
  loading.value = true

  try {
    const usuario = await getUsuario(usuarioId.value)
    form.value = {
      nome: usuario.nome,
      login: usuario.login,
      senha: '',
      tipo: usuario.tipo,
      ativo: usuario.ativo,
      projeto_ids: usuario.projeto_ids ?? [],
    }
  } catch (err) {
    await Swal.fire({
      icon: 'error',
      title: 'Erro ao carregar',
      text: getApiErrorMessage(err, 'Usuário não encontrado'),
    })
    await router.replace('/admin/usuarios')
  } finally {
    loading.value = false
  }
}

onMounted(async () => {
  try {
    projetos.value = await listProjetos()
  } catch {
    projetos.value = []
  }
  await loadUsuario()
})

watch(usuarioId, async () => {
  await loadUsuario()
})

async function onSubmit(): Promise<void> {
  const nome = form.value.nome.trim()
  const login = form.value.login.trim()

  if (nome === '' || login === '') {
    await Swal.fire({
      icon: 'warning',
      title: 'Campos obrigatórios',
      text: 'Informe nome e login.',
    })
    return
  }

  if (!tiposDisponiveis.value.includes(form.value.tipo)) {
    await Swal.fire({
      icon: 'warning',
      title: 'Tipo inválido',
      text: 'Selecione um tipo permitido para o seu perfil.',
    })
    return
  }

  if (isSelf.value && !form.value.ativo) {
    await Swal.fire({
      icon: 'warning',
      title: 'Não permitido',
      text: 'Você não pode desativar o próprio usuário.',
    })
    return
  }

  saving.value = true

  try {
    const payload = {
      nome,
      login,
      tipo: form.value.tipo,
      ativo: form.value.ativo,
      ...(form.value.tipo === 'Admin' ? { projeto_ids: form.value.projeto_ids } : {}),
      ...(form.value.senha !== '' ? { senha: form.value.senha } : {}),
    }

    await updateUsuario(usuarioId.value, payload)

    await Swal.fire({
      icon: 'success',
      title: 'Usuário atualizado',
      timer: 1500,
      showConfirmButton: false,
    })

    await router.push('/admin/usuarios')
  } catch (err) {
    await Swal.fire({
      icon: 'error',
      title: 'Não foi possível salvar',
      text: getApiErrorMessage(err, 'Erro ao atualizar usuário'),
    })
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <AdminLayout>
    <template #title>
      <div class="d-flex flex-wrap align-items-center justify-content-between gap-3">
        <div>
          <h1 class="h5 mb-1">Editar usuário</h1>
          <p class="text-muted small mb-0">
            Altere os dados do usuário.
            <span v-if="isSelf" class="d-block">Você está editando o próprio perfil.</span>
          </p>
        </div>
        <RouterLink class="btn btn-outline-secondary btn-sm" to="/admin/usuarios">
          Voltar
        </RouterLink>
      </div>
    </template>

    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-dark" role="status">
        <span class="visually-hidden">Carregando…</span>
      </div>
    </div>

    <div v-else class="card border-0 shadow-sm">
      <div class="card-body p-4">
        <UsuarioAdminForm
          v-model="form"
          :tipos-disponiveis="tiposDisponiveis"
          :projetos="projetos"
          :saving="saving"
          @submit="onSubmit"
        >
          <template #actions>
            <button
              type="submit"
              class="btn btn-dark"
              :disabled="saving"
            >
              <span
                v-if="saving"
                class="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              />
              {{ saving ? 'Salvando…' : 'Salvar alterações' }}
            </button>
          </template>
        </UsuarioAdminForm>
      </div>
    </div>
  </AdminLayout>
</template>
